import {
  EventConnectionWithId,
  Types,
  Layout,
  PointsEventType,
  SceneModelData,
  SceneCollectedData,
  SceneTimestamps,
  DownloadableFileObject,
  MapInfoCollector,
  MapInfo,
  FileObject,
  deepFindFiles,
  extractFilesFromProp,
  SceneCollectedEvent,
  replaceFileAssets,
  replaceLayoutAssets,
  SceneCollectedActions,
  SceneCollectedAction,
  SceneVariableActions,
  castIfNumeric,
} from "@citychallenge/common";
import { SceneConnectionEvents } from "./scene-connection";
import { observable, computed, runInAction, action } from "mobx";
import { ScenarioModel } from "../scenario/scenario.model";
import { PointsEvent, POINT_SCALES } from "./points-event";
import { generateRandomId } from "@citychallenge/common";
import { createDownloadTask } from "../../download";
import { DownloadStore } from "../../download";
import { DefaultLogger } from "../../logger";
import { SceneMainMedia } from "./scene-main-media.interface";
import { RedirectV2Props } from "../elements/definitions/redirect-v2";
import { LogicBranchProps } from "../elements";

const collector = new MapInfoCollector();

/**
 * How long (ms) the answered state stays visible after a countdown timeout
 * before the scene finishes, when no points/explanation screen follows.
 */
const TIMEOUT_ANSWERED_DELAY = 1500;

export class SceneModel<T extends { [key: string]: any } = {}>
  implements SceneModelData<EventConnectionWithId[]>
{
  public id: string;
  public layout: Layout;

  @observable
  public _hotlinkGameName? = "";

  public get element() {
    return this.layout[0];
  }

  @computed
  public get outputPrefix() {
    return this.element.displayName || this.id.replace("sc_", "");
  }

  @computed
  public get isInvisible() {
    if (this.element.name === "logic-branch") {
      const props = this.element.props as LogicBranchProps;

      if (props.type !== "logic") return false;

      const logicOptions = props.logicOptions ? [...props.logicOptions] : [];
      if (props.logicFallback) {
        logicOptions.push(props.logicFallback);
      }

      for (const option of logicOptions) {
        if (option.action === "branch") return false;
      }

      // If none of the options are  branch option, this scene is invisible
      return true;
    }

    return false;
  }

  @computed
  public get completesScenario() {
    // Redirect complete the scenario
    if (
      this.element.name === "redirect-v2" &&
      this.element.props?.["quitStory"]
    ) {
      return true;
    }

    if (this.element.name === "outcome" && this.element.props?.["quitStory"]) {
      return true;
    }

    if (
      this.element.name === "leaderboard" &&
      this.scenario?._safeIsLastScene(this)
    ) {
      return true;
    }

    return this.element.name === "redirect";
  }

  @computed
  public get introTextId() {
    return `${this.id}-intro-text`;
  }

  @observable
  public name: string;

  public connections: EventConnectionWithId[];
  public bonusWithinSeconds: number;
  public timerDuration: number;
  public maxPoints: number;
  public variableActions: SceneVariableActions;
  public buttonText?: string;

  public insertedOn = +new Date();

  private _timeoutFinishHandle?: ReturnType<typeof setTimeout>;

  @observable
  public skipped?: boolean;

  @observable
  public hidden?: boolean;

  @observable
  public explanationText?: Types["MarkdownText"] | null;

  @action
  public setExplanationText = (text: Types["MarkdownText"] | null) => {
    this.explanationText = text;
  };

  @observable
  public timestamps: SceneTimestamps;

  @observable
  public showingPoints = false;

  @observable
  public lockedUntilDate?: Date;

  @action
  public setLockedUntilDate = (date: Date | undefined) => {
    this.lockedUntilDate = date;
  };

  @observable
  public showingExplanation = false;

  /**
   * The offset on which the points component is visible, used by ElementUI to
   * scroll the points component into view
   */
  @observable
  public pointsYOffset = 0;

  @action
  public setPointsYOffset = (offset: number) => {
    this.pointsYOffset = offset;
  };

  public startedBranches: string[];

  /**
   * The offset on which the explanation component is visible, used by ElementUI
   * to scroll the explanation component into view
   */

  @observable
  public explanationYOffset = 0;

  @observable
  public startedByScenes: string[] = [];

  @action
  public setExplanationYOffset = (offset: number) => {
    this.explanationYOffset = offset;
  };

  @observable
  public state: Partial<T>;

  @observable
  public reinitState?: Partial<T>;

  @observable
  public collectedData: SceneCollectedData;

  @observable
  public collectedEvents: SceneCollectedEvent[];

  @action
  public setState = (state: Partial<T>) => {
    this.state = { ...this.state, ...state };
  };

  @computed
  public get outputData() {
    // Add new output system fallbacks to output data, so that it is recalled
    // when display variables of the game are retrieved
    const outputs = this.element.outputs || {};
    const outputFallbacks: { [key: string]: string } = {};

    for (const value of Object.values(outputs)) {
      if (value?.variable && value.fallback)
        outputFallbacks[`@${value.variable}`] = value.fallback;
    }

    const legacyFallbacks: { [key: string]: string } = {};

    for (const [key, value] of Object.entries(
      this.element.outputFallbacks || {}
    )) {
      if (key !== "default" || !this.element.displayName) continue;
      legacyFallbacks[this.element.displayName] = value;
    }

    return {
      ...outputFallbacks,
      ...legacyFallbacks,
      ...this._outputData,
    };
  }

  @observable
  public _outputData: { [key: string]: string };

  @action
  public setOutputData = (data: { [key: string]: string }) => {
    if (this.element.displayName) {
      const save: { [key: string]: string } = {};

      for (const key of Object.keys(data)) {
        if (key !== "default") continue;

        save[this.element.displayName] = data[key];
      }
      this._outputData = { ...this._outputData, ...save };
    }

    const outputs = this.element.outputs || {};

    for (const [key, value] of Object.entries(data)) {
      const variable = outputs[key]?.variable;
      if (variable)
        this.scenario?.game?.setVariables({ [`@${variable}`]: value });
    }
  };

  /*
   * Scene ended is a state where the scene has no governing scenario
   * and therefore marks itself as finished
   */
  @computed
  public get sceneEnded() {
    return !!this.timestamps.finished;
  }

  public scenario?: ScenarioModel;

  @computed
  public get weight() {
    if (!this.scenario) return -1;
    return this.scenario.scenes.findIndex((scene) => scene.id === this.id);
  }

  @computed
  public get maxBranchLength() {
    if (!this.attachedBranches.length) return 0;

    const branches = [...this.attachedBranches].sort(
      (a, b) => b.numScenes - a.numScenes
    );

    return branches.find((s) => !s.started)?.numScenes || 0;
  }

  @observable
  public branched = false;

  @observable
  private _downloadStore: DownloadStore;

  /**
   * All download tasks that this scene needs to have completed
   */
  public get downloadStore() {
    return this._downloadStore;
  }

  public readonly mapInfo: MapInfo[];

  constructor(data: SceneModelData, scenario?: ScenarioModel) {
    this.scenario = scenario;

    this.collectedData = data.collectedData || {};
    this.collectedEvents = data.collectedEvents || [];
    this.collectedActions = data.collectedActions || {};
    this._outputData = data.outputData || {};

    this.lockedUntilDate = data.lockedUntil
      ? new Date(data.lockedUntil)
      : undefined;

    this.id = data.id;
    if (!this.id) throw new Error("Trying to construct scene without id.");

    this.layout = replaceLayoutAssets(data.layout, data.assets || []);
    // Markdown/explanation files live outside the layout tree; resolve them
    // against the asset list too so they get the same path and metadata.
    replaceFileAssets(
      extractFilesFromProp(data.explanationText),
      data.assets || []
    );
    this.connections = data.connections.map((conn) => {
      if (!conn.id) conn.id = generateRandomId("conn");
      return conn as EventConnectionWithId;
    });
    this.name = data.name;
    this.points = (data.points || []).map((p) => new PointsEvent(p));
    this.state = {};
    this.reinitState = data.reinitState as Partial<T> | undefined;
    if (data.state || data.reinitState)
      Object.assign(this.state, data.state, data.reinitState);
    this.timestamps = data.timestamps || {
      started: 0,
      answered: 0,
      finished: 0,
    };

    this.skipped = data.skipped;
    this.hidden = data.hidden;

    this.branched = data.branched || false;
    this.startedBranches = data.startedBranches || [];
    this.startedByScenes = data.startedByScenes || [];

    this.showingPoints = data.showingPoints || false;
    this.showingExplanation = data.showingExplanation || false;

    this.bonusWithinSeconds = data.bonusWithinSeconds || 0;
    this.timerDuration = data.timerDuration || 0;
    this.explanationText = data.explanationText;
    this.buttonText = data.buttonText ?? undefined;
    this.maxPoints = data.maxPoints || 0;

    this.variableActions = data.variableActions || {};

    this._downloadStore = this.createDownloadStore();

    this.mapInfo = collector.gatherFromScene(this);
  }

  private createDownloadStore() {
    const files = this.getResourceFiles();
    const fileService = this.fileService;

    // Gather all resourceFiles and transform them into download tasks
    // so they can be downloaded before a scene starts
    const tasks = fileService
      ? files.map((file) => createDownloadTask(fileService, file))
      : [];

    return new DownloadStore(tasks);
  }

  @action
  public resetDownloadStore() {
    this._downloadStore = this.createDownloadStore();
  }

  public toJSON(): SceneModelData {
    return {
      id: this.id,
      layout: this.layout,
      connections: this.connections,
      variableActions: this.variableActions,
      name: this.name,
      points: this.points.map((p) => p.toJSON()),
      state: this.state,
      reinitState: this.reinitState,
      timestamps: this.timestamps,
      showingPoints: this.showingPoints,
      showingExplanation: this.showingExplanation,
      collectedData: this.collectedData,
      collectedEvents: this.collectedEvents,
      collectedActions: this.collectedActions,
      outputData: this._outputData,
      bonusWithinSeconds: this.bonusWithinSeconds,
      timerDuration: this.timerDuration,
      maxPoints: this.maxPoints,
      branched: this.branched,
      startedBranches: this.startedBranches,
      startedByScenes: this.startedByScenes,
      explanationText: this.explanationText,
      buttonText: this.buttonText,
      skipped: this.skipped,
      hidden: this.hidden,
      lockedUntil: this.lockedUntilDate && this.lockedUntilDate.toString(),
    };
  }

  public onUpdateConnection = (conn: EventConnectionWithId) => {
    const connection = this.connections.find((c) => c.id === conn.id);
    if (!connection) return;
    connection.completed = conn.completed;
  };

  @action
  private _onStart = () => {
    this._setTimestamp("started");
  };

  @action
  private _onAnswer = () => {
    this._setTimestamp("answered");

    // Handle the actions that have an active trigger
    this._handleActions();

    if (this.skipped || !this.maxPoints) return;

    // Calculate points
    this._addPoints(PointsEventType.correct);

    // Calculate timebonus
    if (
      this.bonusWithinSeconds &&
      this.timestamps.answered - this.timestamps.started <
        this.bonusWithinSeconds &&
      this.hintsUsedPenalty >= 0 &&
      this.wrongAnswersPenalty >= 0
    ) {
      this._addPoints(PointsEventType.timeBonus, POINT_SCALES.timeBonus);
    }
  };

  private _handleActions = () => {
    for (const key of Object.keys(this.collectedActions)) {
      const action = this.collectedActions[key];

      runInAction(() => {
        if (action.trigger && !action.handled) {
          this._handleAction(key, action);
        }
      });
    }
  };

  private _handleAction(
    key: string,
    { trigger, value, handled, skipVariables }: SceneCollectedAction
  ) {
    if (!trigger || handled) return;

    const currentParams = this.scenario?.game?.variables || {};
    const variableActions = this.variableActions[key] || [];
    const params: { [key: string]: string } = {};

    for (const action of variableActions) {
      if (action.variableId && skipVariables?.includes(action.variableId))
        continue;
      const variableId = `@${action.variableId}`;

      if (value !== undefined) {
        if (action.action === "assign-add") {
          params[variableId] = `${
            castIfNumeric(currentParams[variableId], 0) +
            castIfNumeric(value, 0)
          }`;
        } else {
          params[variableId] = value || action.fallback || "";
        }
        continue;
      }

      if (action.action === "set") {
        params[variableId] = `${action.value}`;
      }

      if (action.action === "append") {
        params[variableId] = `${currentParams[variableId] || ""}${
          action.value ?? ""
        }`;
      }

      if (action.action === "add") {
        params[variableId] = `${
          castIfNumeric(currentParams[variableId], 0) +
          castIfNumeric(action.value, 0)
        }`;
      }

      if (action.action === "subtract") {
        params[variableId] = `${
          castIfNumeric(currentParams[variableId], 0) -
          castIfNumeric(action.value, 0)
        }`;
      }
    }

    this.scenario?.game?.setVariables(params);
  }

  @computed
  public get attachedBranches() {
    if (!this.connections?.length) return [];

    const branches = this.scenario?.game?.branches;

    if (!branches?.length) return [];

    return this.connections
      .filter((value) => value.to.prop === "startBranch")
      .map((value) => {
        return branches.find((scenario) => value.to.value === scenario.id);
      })
      .filter((v) => !!v) as ScenarioModel[];
  }

  public resetScenarioToSelf() {
    // Scenario was branched before, so we will remove those branches
    this.scenario?.filterScenes((scene) => {
      return !scene.startedByScenes?.includes(this.id);
    });

    this.scenario?.resetScenarioTo(this.id);
  }

  /**
   * Return the main media of the scene, if any. This is used to preload images
   * and videos before the scene starts. Especially useful for branched scenes.
   */
  @computed
  public get mainMedia(): SceneMainMedia | undefined {
    if (this.element.props?.["headerImage"]) {
      return {
        preset: "header",
        file: this.element.props["headerImage"],
      };
    }

    if (this.element.props?.["video"]) {
      return {
        preset: "video",
        file: this.element.props["video"],
      };
    }

    if (
      this.element.name === "true-or-false" &&
      this.element.props?.["image"]
    ) {
      return {
        preset: "header",
        file: this.element.props["image"],
      };
    }

    if (
      (this.element.name === "image-hotspots" ||
        this.element.name === "hotspot-branch") &&
      this.element.props?.["image"]
    ) {
      return {
        preset: "image-hotspots",
        file: this.element.props["image"],
      };
    }

    if (this.element.props["media"]) {
      return {
        preset: "header",
        file: this.element.props["media"],
      };
    }

    if (this.element.props["trackable"]?.source) {
      return {
        preset: "header",
        file: this.element.props["trackable"]?.source,
        disableBranchPreload: true,
      };
    }

    if (this.element.props["renderable"]?.source) {
      return {
        preset: "header",
        file: this.element.props["renderable"]?.source,
        disableBranchPreload: true,
      };
    }

    if (this.element.name === "redirect") {
      const image =
        this.scenario?.game?.storyIcon || this.scenario?.game?.image;

      if (!image) return undefined;
      return {
        preset: "header",
        file: {
          path: image,
          pathType: "remote",
        },
      };
    }

    if (this.element.name === "redirect-v2") {
      const fallback =
        this.scenario?.game?.storyIcon || this.scenario?.game?.image;
      const props: RedirectV2Props = this.element.props;

      for (const item of props.options || []) {
        const image = item.image?.path || item.story?.image?.path;
        if (image)
          return {
            preset: "header",
            file: {
              path: image,
              pathType: "remote",
            },
          };
      }
      if (fallback)
        return {
          preset: "header",
          file: {
            path: fallback,
            pathType: "remote",
          },
        };
    }

    return;
  }

  @action
  public __dangerouslyHideSelfFromScenario = () => {
    this.hidden = true;
  };

  /**
   * Use this function only to start branches from the outside of normal
   * 'onAnswer' and event flow. This is a dangerous function and should only
   * be used in specific cases.
   * @param branchId
   */
  @action
  public __dangerouslyInsertBranch = (
    branchId: string | null,
    replace = false
  ) => {
    this._setTimestamp("started");

    if (replace) {
      this.__dangerouslyHideSelfFromScenario();
    }

    if (branchId === null) {
      return;
    }

    const branch = this.scenario?.game?.branches?.find(
      (branch) => branch.id === branchId
    );

    this.branched = true;

    if (!branch) {
      DefaultLogger.captureObject("Could not insert branch", {
        branchId: branch,
        gameId: this.scenario?.game?.id,
        sceneId: this.id,
      });

      return { scenes: undefined, branch: undefined };
    }

    branch.setStarted(true);

    const scenes = branch.scenes.map((scene) =>
      SceneModel.resetModel(scene, this.scenario, () => ({
        id: `${scene.id}_${this.scenario?.scenes.length}`,
        startedByScenes: [...this.startedByScenes, this.id],
      }))
    );
    this.scenario?.insertScenes(scenes, 0, this.id);
    return { scenes, branch };
  };

  public __dangerouslyStartBranch = (
    branchId: string,
    customBranchIds?: string[]
  ) => {
    this._startBranch(branchId, customBranchIds);
  };

  /**
   * After a branch's scenes play, handle its end-of-branch mode.
   * Jump and loopback are the same operation: resolve a target,
   * ensure its trailing siblings exist, chain if the target's
   * branch also has a mode.
   */
  public __dangerouslyInsertLoopsAndJumps(
    branch: ScenarioModel,
    customBranchIds?: string[]
  ) {
    const target = this._resolveBranchTarget(branch, customBranchIds);
    if (!target) return;

    if (branch.mode === "jump") {
      this._removeUnstartedSiblings();
    }

    // De-dupe: if an unstarted instance of the target's content already sits
    // forward of this scene (e.g. a reset-copy left by prior branch activity
    // that `_removeUnstartedSiblings` couldn't reach because its ancestry
    // differs), reuse it instead of inserting a second copy. Without this,
    // jumping forward to an already-queued scene plays it twice.
    if (branch.mode === "jump" && this._hasUnstartedForwardInstance(target)) {
      return;
    }

    const scenes = this._buildPath(target, branch);
    if (scenes.length) {
      this.scenario?.insertScenes(scenes, branch.scenes.length);
    }
  }

  private _resolveBranchTarget(
    branch: ScenarioModel,
    customIds?: string[]
  ): SceneModel | undefined {
    if (branch.mode === "jump" && branch.jumpTarget) {
      return this._resolveTarget(branch.jumpTarget);
    }

    if (branch.mode === "loopback") {
      const available = this._getUnstartedBranches(branch, customIds);
      return available.length ? this : undefined;
    }

    return undefined;
  }

  /** Build target + missing siblings, then follow cross-branch chain. */
  private _buildPath(target: SceneModel, branch: ScenarioModel): SceneModel[] {
    const result: SceneModel[] = [];
    const visited = new Set<string>([branch.id]);

    result.push(this._resetSceneForJump(target, branch.id));
    this._pushMissingSiblings(result, target, branch.id);

    let currentBranch =
      target.scenario !== this.scenario ? target.scenario : undefined;

    while (currentBranch && currentBranch.mode !== "default") {
      if (visited.has(currentBranch.id)) break;
      visited.add(currentBranch.id);
      currentBranch.setStarted(true);

      const chainTarget = this._resolveChainTarget(currentBranch);
      if (!chainTarget) break;

      // Only difference: loopback tracks its own branch; jump accumulates
      result.push(
        currentBranch.mode === "loopback"
          ? this._resetSceneForJump(chainTarget, currentBranch.id, [
              currentBranch.id,
            ])
          : this._resetSceneForJump(chainTarget, branch.id)
      );
      this._pushMissingSiblings(result, chainTarget, branch.id);

      if (currentBranch.mode === "loopback") break;

      currentBranch =
        chainTarget.scenario !== this.scenario
          ? chainTarget.scenario
          : undefined;
    }

    return result;
  }

  /** Find a scene by ID — searches active scenario, game-wide, then _originalScenes. */
  private _resolveTarget(targetId: string): SceneModel | undefined {
    return (
      this.scenario?.findNearestSceneStartingWith(targetId) ||
      this.scenario?.game?.findSceneById(targetId) ||
      this.scenario?._originalScenes?.find((s) => s.id === targetId)
    );
  }

  private _resolveChainTarget(branch: ScenarioModel): SceneModel | undefined {
    if (branch.mode === "jump" && branch.jumpTarget) {
      return this._resolveTarget(branch.jumpTarget);
    }

    if (branch.mode === "loopback" && branch.scene?.id) {
      const parent = this._resolveTarget(branch.scene.id);
      if (!parent) return undefined;
      const available = parent.attachedBranches.filter(
        (b) => b.scenes.length && b.id !== branch.id
      );
      return available.length ? parent : undefined;
    }

    return undefined;
  }

  /**
   * Map a (possibly reset-copied) scene back to the id of the original scene it
   * derives from. Reset-copies are minted as `${source.id}_${len}` by
   * `_resetSceneForJump`, so we match against the original scene ids and pick
   * the longest match (the `_` separator avoids `sc_Q` matching `sc_Qx`).
   */
  private _canonicalId(scene: SceneModel): string {
    const originals = this.scenario?._originalScenes ?? this.scenario?.scenes;
    const match = (originals || [])
      .filter((o) => scene.id === o.id || scene.id.startsWith(`${o.id}_`))
      .sort((a, b) => b.id.length - a.id.length)[0];
    return match?.id ?? scene.id;
  }

  /**
   * True when an unstarted scene sharing the target's canonical content already
   * sits forward of this scene. Used to avoid inserting a duplicate when a jump
   * target is already queued next.
   */
  private _hasUnstartedForwardInstance(target: SceneModel): boolean {
    const scenes = this.scenario?.scenes || [];
    const myIdx = scenes.findIndex((s) => s.id === this.id);
    const forward = myIdx === -1 ? scenes : scenes.slice(myIdx + 1);
    const targetCanonical = this._canonicalId(target);
    return forward.some(
      (s) => !s.isStarted && this._canonicalId(s) === targetCanonical
    );
  }

  /**
   * Find what should follow the target in the original source,
   * return only what's not already present (unstarted) in the active scenario.
   *
   * Base scenario uses _originalScenes (pristine snapshot from first launch).
   * Branches use .scenes directly (they're never mutated by jump logic).
   */
  private _getMissingSiblings(target: SceneModel): SceneModel[] {
    const allScenarios = [
      this.scenario,
      ...(this.scenario?.game?.branches || []),
    ];

    for (const src of allScenarios) {
      if (!src) continue;
      const sourceScenes = src._originalScenes || src.scenes;
      const idx = sourceScenes.findIndex(
        (s) => target.id === s.id || target.id.startsWith(`${s.id}_`)
      );
      if (idx === -1) continue;

      return sourceScenes
        .slice(idx + 1)
        .filter(
          (sibling) =>
            !this.scenario?.scenes.some(
              (s) =>
                !s.isStarted &&
                (s.id === sibling.id || s.id.startsWith(`${sibling.id}_`))
            )
        );
    }

    return [];
  }

  private _pushMissingSiblings(
    result: SceneModel[],
    target: SceneModel,
    branchId: string
  ) {
    for (const s of this._getMissingSiblings(target)) {
      result.push(this._resetSceneForJump(s, branchId));
    }
  }

  private _removeUnstartedSiblings() {
    const myAncestry = JSON.stringify(this.startedByScenes);
    const currentIdx =
      this.scenario?.scenes.findIndex((s) => s.id === this.id) ?? -1;

    // If scene is hidden (e.g. logic-branch), it won't appear in the
    // filtered scenes getter. Treat ALL same-ancestry unstarted scenes
    // as forward — they all come after a hidden scene.
    const forwardScenes =
      currentIdx !== -1
        ? this.scenario?.scenes.slice(currentIdx + 1) || []
        : this.scenario?.scenes || [];

    const forwardIds = new Set<string>();
    for (const scene of forwardScenes) {
      if (
        !scene.isStarted &&
        JSON.stringify(scene.startedByScenes) === myAncestry
      ) {
        forwardIds.add(scene.id);
      }
    }

    this.scenario?.filterScenes((scene) => {
      if (forwardIds.has(scene.id)) return false;
      if (
        !scene.isStarted &&
        scene.startedByScenes.length > 0 &&
        JSON.stringify(scene.startedByScenes) === myAncestry
      ) {
        return false;
      }
      return true;
    });
  }

  private _resetSceneForJump(
    source: SceneModel,
    branchId: string,
    startedBranches?: string[]
  ): SceneModel {
    return SceneModel.resetModel(source, this.scenario, (sceneData) => ({
      id: `${source.id}_${this.scenario?.scenes.length}`,
      startedBranches: startedBranches || [...this.startedBranches, branchId],
      startedByScenes: [...this.startedByScenes, this.id],
      reinitState: { ...sceneData.state, activeOption: undefined },
    }));
  }

  private _getUnstartedBranches(
    exclude: ScenarioModel,
    customIds?: string[]
  ): ScenarioModel[] {
    const candidates = customIds?.length
      ? this.scenario?.game?.branches?.filter((b) =>
          customIds.includes(b.id)
        ) || []
      : this.attachedBranches;

    return candidates.filter(
      (b) =>
        b.scenes.length &&
        !this.startedBranches.includes(b.id) &&
        b.id !== exclude.id
    );
  }

  @action
  private _startBranch = (branchId: string, selectedOptionIds?: string[]) => {
    this.setState({
      ...this.state,
      branchEventFired: true,
    });

    // First find branch inside game info
    const branch = this.scenario?.game?.branches?.find(
      (branch) => branch.id === branchId
    );

    if (!branch) {
      DefaultLogger.captureObject("Could not start branch", {
        branchId: branch,
        gameId: this.scenario?.game?.id,
        sceneId: this.id,
      });

      this._onContinue(true);
      return;
    }

    this.collectEvent({ event: "start-branch", value: branchId });

    if (this.skipped || this.branched || this.isFinished) {
      this.resetScenarioToSelf();
    }

    this.branched = true;

    branch.setStarted(true);

    this.scenario?.insertScenes(
      branch.scenes.map((scene) =>
        SceneModel.resetModel(scene, this.scenario, () => ({
          id: `${scene.id}_${this.scenario?.scenes.length}`,
          startedByScenes: [...this.startedByScenes, this.id],
        }))
      )
    );

    this.__dangerouslyInsertLoopsAndJumps(branch, selectedOptionIds);

    this._onContinue(true);
  };

  @action
  private _onContinue = (force = false) => {
    // Guards against finishing a scene twice: once finished, a non-forced
    // continue is a no-op (and `_setTimestamp` below never overwrites either).
    if (this.isFinished && !force) return;

    if (!this.isAnswered) this._onAnswer();

    if (this.totalPoints && !this.showingPoints) {
      this.showingPoints = true;
      if (!this.skipped) return;
    }

    const hasExplanation = this.explanationText && this.explanationText.text;
    if (hasExplanation && !this.showingExplanation) {
      this.showingExplanation = true;
      if (!this.skipped) return;
    }

    // Cancel any pending delayed timeout-finish, so it can't fire again.
    this._clearTimeoutFinish();

    this._setTimestamp("finished");
    if (this.scenario) {
      this.scenario.onSceneFinish(this);
    }
  };

  private _clearTimeoutFinish = () => {
    if (this._timeoutFinishHandle) {
      clearTimeout(this._timeoutFinishHandle);
      this._timeoutFinishHandle = undefined;
    }
  };

  /**
   * Called when the block's countdown timer runs out. The user is given the
   * maximal wrong-answer penalty (but still keeps the lowest possible points),
   * the block is answered, and then it continues.
   *
   * To let the player briefly see the answered state, the finish is delayed by
   * `TIMEOUT_ANSWERED_DELAY`. That delay is skipped when a points or explanation
   * screen will be shown, since those already require an extra tap.
   */
  @action
  private _onTimeout = () => {
    if (this.isAnswered || this.isFinished) return;

    if (this.maxPoints) {
      this.points.push(
        new PointsEvent({
          points: POINT_SCALES.wrongSingleAttempt[0] * this.maxPoints,
          type: PointsEventType.wrong,
        })
      );
    }

    // Answer now so the answered state becomes visible to the player.
    this._onAnswer();

    const willShowPoints = !!this.totalPoints && !this.showingPoints;
    const hasExplanation = !!(
      this.explanationText && this.explanationText.text
    );
    const willShowExplanation = hasExplanation && !this.showingExplanation;

    if (willShowPoints || willShowExplanation) {
      this._onContinue();
      return;
    }

    this._timeoutFinishHandle = setTimeout(
      () => this._onContinue(),
      TIMEOUT_ANSWERED_DELAY
    );
  };

  public readonly lifecycle = {
    onStart: () => this._onStart(),
    onAnswer: () => this._onAnswer(),
    continue: () => this._onContinue(),
    forceContinue: () => this._onContinue(true),
    timeout: () => this._onTimeout(),
  };

  /**
   * The scene length (this scene plus longest branch)
   */
  @computed
  public get sceneLength() {
    return this.branched || this.skipped || this.isFinished
      ? 1
      : 1 + this.maxBranchLength;
  }

  @action
  public skip = () => {
    if (!this.skipped && !this.isAnswered) {
      // Run listeners, and if one returns true, assume that the skip has
      // been handled somewhere else, or blocked
      if (this.runListeners("skip")) return;

      this.points.splice(0, this.points.length);
      this.skipped = true;
    }

    this._onContinue();
  };

  public readonly events: SceneConnectionEvents = {
    startBranch: (branchId: string) => this._startBranch(branchId),
    onFinish: () => this._onContinue(),
  };

  @action
  public collect = (data: SceneCollectedData) => {
    this.collectedData = { ...this.collectedData, ...data };
  };

  @action
  public collectEvent = (event: Omit<SceneCollectedEvent, "timestamp">) => {
    const existing = this.collectedEvents || [];
    this.collectedEvents = [
      ...existing,
      { ...event, timestamp: Math.floor(Date.now() / 1000) },
    ];
  };

  /**
   * Collected actions store which actions must be triggered on answer or skip
   * of the scene. Actions can only be executed once.
   */
  @observable
  public collectedActions: SceneCollectedActions = {};

  @action
  public collectAction = (
    action: string,
    trigger: boolean,
    value?: string,
    skipVariables?: string[]
  ) => {
    this.collectedActions[action] = { trigger, value, skipVariables };
  };

  @action
  public collectActionImmediate = (
    action: string,
    trigger: boolean,
    value?: string
  ) => {
    this.collectedActions[action] = { trigger, value, handled: true };

    if (trigger) {
      this._handleAction(action, { trigger, value });
    }
  };

  @computed
  public get isActive() {
    if (!this.scenario) return true;
    return this.scenario.activeSceneId === this.id;
  }

  @computed
  public get isStarted() {
    return !!this.timestamps.started;
  }

  @computed
  public get isAnswered() {
    return !!this.timestamps.answered;
  }

  @computed public get isCompleted() {
    return this.isAnswered;
  }

  @computed
  public get isFinished() {
    return !!this.timestamps.finished;
  }

  private _setTimestamp(timestamp: keyof SceneTimestamps) {
    if (this.timestamps[timestamp]) return;
    this.timestamps[timestamp] = Math.floor(Date.now() / 1000);
  }

  // Points and hint system
  @observable
  public readonly points: PointsEvent[] = [];

  /**
   * Unlock a hunt and add it to the point events
   */
  @action
  public unlockHint = () => {
    this._addPoints(PointsEventType.hint, POINT_SCALES.hint);
  };

  /**
   * Add a wrong answer to the point events
   */
  @action
  public addWrongAnswer = (scale = POINT_SCALES.wrong) => {
    this._addPoints(PointsEventType.wrong, scale);
  };

  /**
   * Add a points with given scales.
   *
   * Number of entries in the points array
   * can never exceed the length of the scales arrayt.
   */
  private _addPoints(type: PointsEventType, scales = [1]) {
    const current = this._numEntriesOfPointsType(type);
    const scaleValue = scales[current] || 0;

    this.points.push(
      new PointsEvent({
        points: scaleValue * this.maxPoints,
        type,
      })
    );
  }

  private _numEntriesOfPointsType(type: PointsEventType) {
    return this.points.filter((p) => p.type == type).length;
  }

  /**
   * Number of hints used
   */
  @computed public get hintsUsed() {
    return this._numEntriesOfPointsType(PointsEventType.hint);
  }

  /**
   * Number of wrong ansers given
   */
  @computed public get wrongAnswers() {
    return this._numEntriesOfPointsType(PointsEventType.wrong);
  }

  /**
   * Sum points of a given type to a total (omit type to sum all points)
   */
  private _sumPoints(type?: PointsEventType) {
    let points = this.points;
    if (type) {
      points = points.filter((p) => p.type == type);
    }
    return points.reduce<number>((prevP, points) => prevP + points.points, 0);
  }

  /**
   * Time bonus received
   */
  @computed public get correctAnswerPoints() {
    return this._sumPoints(PointsEventType.correct);
  }

  /**
   * Total penalty on points of all hints used
   */
  @computed public get hintsUsedPenalty() {
    return this._sumPoints(PointsEventType.hint);
  }

  /**
   * Total penalty of points of all wrong answers given
   */
  @computed public get wrongAnswersPenalty() {
    return this._sumPoints(PointsEventType.wrong);
  }

  /**
   * Time bonus received
   */
  @computed public get timeBonusPoints() {
    return this._sumPoints(PointsEventType.timeBonus);
  }

  /**
   * Total of all points scored by the user
   */
  @computed public get totalPoints() {
    if (!this.maxPoints) return 0;
    return this._sumPoints();
  }

  /**
   * Wether or not this scene is running the hotlink/developer mode feature
   * of the platform
   */
  @computed
  public get isRunningInHotlink() {
    return !this.scenario || this.scenario.isRunningInHotlink;
  }

  /**
   * Check if element is locked by `lockedUntil`
   *
   * Note: Not computed because it uses current date as calculation.
   */
  public get isTimeLocked() {
    if (!this.lockedUntilDate) return false;

    return this.lockedUntilDate > new Date();
  }

  public get fileCollector() {
    return this.scenario?.game?.fileCollector;
  }

  public get fileService() {
    return this.scenario?.game?.fileService;
  }

  /**
   * All files for this scvene that are of type 'resource' and have their
   * originalPath defined
   */
  public getResourceFiles(): DownloadableFileObject[] {
    const files =
      this.fileCollector?.gatherSceneFiles(this).filter((object) => {
        return object.pathType === "resource" && object.originalFilePath;
      }) ?? [];

    return files as DownloadableFileObject[];
  }

  public getFiles(): FileObject[] {
    return [
      ...deepFindFiles(this.layout),
      ...extractFilesFromProp(this.explanationText),
    ];
  }

  @computed
  public get failedDownloadTasks() {
    return this.downloadStore.failedDownloadTasks;
  }

  private _listeners: { event: string; fn: () => boolean }[] = [];

  public on = (event: "skip", fn: () => boolean) => {
    // Attach event listener
    this._listeners.push({ event, fn });

    return () => {
      this._listeners = this._listeners.filter(
        (obj) => obj.event !== event && obj.fn !== fn
      );
    };
  };

  private runListeners = (event: "skip") => {
    let hasHandled = false;

    for (const listener of this._listeners) {
      if (listener.event !== event) continue;

      const result = listener.fn();

      if (result) hasHandled = true;
    }

    return hasHandled;
  };

  public static resetModel(
    model: SceneModel,
    scenario?: ScenarioModel,
    overrides?: (sceneData: SceneModelData) => Partial<SceneModelData>
  ) {
    const sceneData = model.toJSON();
    return new SceneModel(
      {
        ...sceneData,
        timestamps: undefined,
        showingExplanation: undefined,
        showingPoints: undefined,
        connections: sceneData.connections.map((connection) => ({
          ...connection,
          completed:
            model.element.name === "map" ? connection.completed : false,
        })),
        state: undefined,
        collectedEvents: [],
        collectedData: undefined,
        collectedActions: {},
        points: [],
        skipped: false,
        branched: false,
        hidden: false,
        ...overrides?.(sceneData),
      },
      scenario
    );
  }
}
